import { inject, Injectable } from '@angular/core';
import {
  App,
  CreateKnowledgeSourceRequestInterface,
  DeleteKnowledgeSourceRequestInterface,
  KnowledgeBaseService,
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigInterface,
  KnowledgeSourceConfigType,
  KnowledgeSourceInterface,
  ListAllAppsForKnowledgeSourceRequestInterface,
  ListAllKnowledgeSourcesForAppRequestInterface,
  ListKnowledgeSourcesRequestInterface,
  ListKnowledgeSourcesResponse,
  NamespaceInterface,
  PagedRequestOptionsInterface,
  UpdateKnowledgeSourceRequestInterface,
  UpsertKnowledgeForAppRequestInterface,
  WebsiteScrapeConfigCrawlMode,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import { catchError, firstValueFrom, map, of, shareReplay, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
} from './tokens';
import { TranslateService } from '@ngx-translate/core';
import { RouteCommand } from './interface/config.interface';
import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';
import { SetAppsForKnowledgeSourceRequestInterface } from '@vendasta/embeddings/lib/_internal/interfaces/api.interface';
import { AIAssistantAppType, KnowledgeSourceWithSuggestedFlag, WebChatWidgetAppType } from './ai-knowledge-util';
import { FeatureFlagService } from '@galaxy/partner';
import { GoalApiService } from '@vendasta/ai-assistants';

dayjs.extend(UTC);

const AI_ASSISTANTS_FEATURE_FLAG = 'ai_assistant_feature';
const VENDASTA_PARTNER_ID = 'VA';
@Injectable()
export class AiKnowledgeService {
  private readonly knowledgeBaseService = inject(KnowledgeBaseService);
  private readonly translateService = inject(TranslateService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly goalApiService = inject(GoalApiService);

  readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  readonly marketId = toSignal(inject(MARKET_ID_TOKEN));
  readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly manageKnowledgeUrl = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private readonly businessProfileUrl = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private readonly webChatWidgetEditRoute = toSignal(inject(WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN));

  async getApp(id: string, includeKnowledgeBaseKeys?: boolean): Promise<App> {
    return await firstValueFrom(
      this.knowledgeBaseService.getApp({
        id: id,
        namespace: this.buildNamespaceInterface(),
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async listAllApps(includeKnowledgeBaseKeys?: boolean): Promise<App[]> {
    let cursor = '';
    let hasMore = true;
    const apps: App[] = [];
    while (hasMore) {
      const response = await firstValueFrom(
        this.knowledgeBaseService.listApps({
          filters: {
            namespace: this.buildNamespaceInterface(),
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: 100,
          },
          includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
        }),
      );

      cursor = response.metadata.nextCursor;
      hasMore = response.metadata.hasMore;

      apps.push(...(response.apps || []));
    }

    return await this.filterAIAssistantsInApps(apps);
  }

  private async filterAIAssistantsInApps(apps: App[]): Promise<App[]> {
    const ffResults = await firstValueFrom(
      this.featureFlagService.batchGetStatus(this.partnerId(), this.marketId(), [AI_ASSISTANTS_FEATURE_FLAG]),
    );
    const aiAssistantsEnabled = ffResults[AI_ASSISTANTS_FEATURE_FLAG];
    const goalsDisabled = await firstValueFrom(
      this.goalApiService
        .goalsDisabledForAccountGroup({
          accountGroupId: this.accountGroupId(),
        })
        .pipe(
          map((resp) => !!resp.goalsDisabled),
          catchError(() => of(false)),
          shareReplay(1),
        ),
    );
    return apps.filter(
      (app) => app.appType != (aiAssistantsEnabled && !goalsDisabled ? WebChatWidgetAppType : AIAssistantAppType),
    );
  }

  async getKnowledgeSource(
    id: string,
    namespace: NamespaceInterface,
    includeKnowledgeBaseKeys?: boolean,
  ): Promise<KnowledgeSource> {
    return await firstValueFrom(
      this.knowledgeBaseService.getKnowledgeSource({
        id: id,
        namespace: namespace,
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  // TODO paging
  async listKnowledgeSources(
    configTypes?: KnowledgeSourceConfigType[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Promise<ListKnowledgeSourcesResponse> {
    const request: ListKnowledgeSourcesRequestInterface = {
      filters: {
        namespace: this.buildNamespaceInterface(),
        configTypes: configTypes,
      },
      pagingOptions: pagingOptions,
    };
    return await firstValueFrom(this.knowledgeBaseService.listKnowledgeSources(request));
  }

  async updateKnowledgeSourceNameAndConfig(source: KnowledgeSource): Promise<void> {
    const request: UpdateKnowledgeSourceRequestInterface = {
      knowledgeSource: source,
      fieldMask: {
        paths: ['name', 'config'],
      },
    };
    await firstValueFrom(this.knowledgeBaseService.updateKnowledgeSource(request));
  }

  async createKnowledgeSource(name: string, config: KnowledgeSourceConfigInterface): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async createOrUpdateKnowledgeSource(
    knowledgeSource: KnowledgeSource,
    name: string,
    config: KnowledgeSourceConfigInterface,
  ): Promise<string> {
    if (knowledgeSource) {
      const source = knowledgeSource;
      source.name = name;
      source.config = config as KnowledgeSourceConfig;
      await this.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    } else {
      return await this.createKnowledgeSource(name, config);
    }
  }

  async deleteKnowledgeSource(id: string): Promise<void> {
    const request: DeleteKnowledgeSourceRequestInterface = {
      id: id,
      namespace: this.buildNamespaceInterface(),
    };
    await firstValueFrom(this.knowledgeBaseService.deleteKnowledgeSource(request));
  }

  async listAllAppsForKnowledgeSource(knowledgeSourceId: string): Promise<App[]> {
    const request: ListAllAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceId: knowledgeSourceId,
      namespace: this.buildNamespaceInterface(),
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllAppsForKnowledgeSource(request));
    return await this.filterAIAssistantsInApps(results.apps || []);
  }

  async listAllKnowledgeSourcesForApp(
    appId: string,
    configTypes?: KnowledgeSourceConfigType[],
  ): Promise<KnowledgeSource[]> {
    const request: ListAllKnowledgeSourcesForAppRequestInterface = {
      appId: appId,
      namespace: this.buildNamespaceInterface(),
      filters: {
        configTypes: configTypes,
      },
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllKnowledgeSourcesForApp(request));
    return results.knowledgeSources || [];
  }

  async upsertKnowledgeForApp(
    appId: string,
    name: string,
    configurationUrl: string,
    appType: string,
    knowledgeSources?: KnowledgeSourceInterface[],
  ): Promise<void> {
    const knowledgeSourceKeys = [];

    for (const source of knowledgeSources) {
      knowledgeSourceKeys.push({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
      });
    }

    const request: UpsertKnowledgeForAppRequestInterface = {
      app: {
        id: appId,
        namespace: this.buildNamespaceInterface(),
        name: name,
        configurationUrl: configurationUrl,
        appType: appType,
      },
      knowledgeSourceKeys: knowledgeSourceKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.upsertKnowledgeForApp(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  async setAppsForKnowledgeSource(knowledgeSourceId: string, apps: App[]): Promise<void> {
    const appKeys = [];

    for (const app of apps) {
      appKeys.push({
        appId: app.id,
        namespace: app.namespace,
      });
    }

    const request: SetAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceKey: {
        knowledgeSourceId: knowledgeSourceId,
        namespace: this.buildNamespaceInterface(),
      },
      appKeys: appKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.setAppsForKnowledgeSource(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  buildNamespaceInterface(): NamespaceInterface {
    if (this.accountGroupId()) {
      return {
        accountGroupNamespace: {
          accountGroupId: this.accountGroupId(),
        },
      };
    }
    if (this.partnerId() && this.partnerId() !== VENDASTA_PARTNER_ID) {
      return {
        partnerNamespace: {
          partnerId: this.partnerId(),
        },
      };
    }
    return {
      adminNamespace: {},
    };
  }

  buildManageKnowledgeUrl(): string {
    return this.manageKnowledgeUrl();
  }

  buildBusinessProfileUrl(): string {
    return this.businessProfileUrl();
  }

  isWebChatCreateRouteSet(): boolean {
    return this.webChatWidgetEditRoute()?.length > 0;
  }

  buildWebChatWidgetEditRoute(widgetId: string): RouteCommand[] {
    return this.webChatWidgetEditRoute().map((segment) => {
      if (typeof segment === 'string') {
        return segment === ':id' ? widgetId : segment;
      }
      if (segment['outlets']) {
        for (const [key, value] of Object.entries(segment['outlets'])) {
          if (typeof value === 'string') {
            segment['outlets'][key] = value === ':id' ? widgetId : value;
          } else {
            const valueArray = value as string[];
            segment['outlets'][key] = valueArray.map((item) => (item === ':id' ? widgetId : item));
          }
        }
      }
      return segment;
    });
  }

  buildBusinessProfileKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-profile-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessProfileKnowledgeSource(shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (this.accountGroupId()) {
      return {
        id: this.buildBusinessProfileKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Profile', // untranslated because that is how the name is in the backend
        config: {
          businessProfileConfig: {
            accountGroupId: this.accountGroupId(),
          },
        },
        isSuggested: shouldSuggest,
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  buildBusinessWebsiteKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-website-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessWebsiteKnowledgeSource(url: string, shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (url) {
      return {
        id: this.buildBusinessWebsiteKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Website', // untranslated because that is how the name is in the backend
        config: {
          websiteScrapeConfig: {
            url: url,
            crawlMode: WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
            scrapeProgress: WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED,
          },
        },
        isSuggested: shouldSuggest,
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      return this.translateService.instant('INBOX.WEBCHAT.SETTINGS.BUSINESS_PROFILE.DESCRIPTION');
    }
    if (source?.config?.customDataConfig) {
      return source?.config?.customDataConfig?.text;
    }
    if (source?.config?.websiteScrapeConfig) {
      return source?.config?.websiteScrapeConfig?.url;
    }

    return '';
  }

  getLastScraped(source: KnowledgeSource): string {
    const lastScraped = source?.config?.websiteScrapeConfig?.lastScraped;
    if (!lastScraped) {
      return 'unknown';
    }

    return dayjs.utc(lastScraped).format('MMM D, YYYY');
  }

  async rescrapeWebsite(source: KnowledgeSource): Promise<void> {
    await firstValueFrom(
      this.knowledgeBaseService.scrapeWebsite({ knowledgeSourceId: source.id, namespace: source.namespace }),
    );
  }

  getAppDisplayName(app: App): string {
    if (app.appTypeName) {
      return app.name + ' • ' + app.appTypeName;
    }
    return app.name;
  }

  async getOtherAIDisclaimerText(knowledgeSourceId: string, currentAppId: string): Promise<string> {
    if (knowledgeSourceId && currentAppId) {
      const apps = await this.listAllAppsForKnowledgeSource(knowledgeSourceId);
      const filteredApps = apps.filter((app) => currentAppId != app.id);
      if (filteredApps.length > 0) {
        return this.translateService.instant('EDIT_KNOWLEDGE.OTHER_AI', { count: filteredApps.length });
      }
    }
    return '';
  }
}
